// src/services/adoptionService.ts
// キャラクターおむかえ（販売/購入）サービス

import { config } from '../config';
import { auth } from './firebase';

// Helper to get auth token
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.getIdToken();
}

// ========== Types ==========

export interface SaleSettings {
  forSale: boolean;
  salePrice?: number; // 1,000〜1,000,000円
}

export interface SaleSettingsResponse {
  forSale: boolean;
  salePrice?: number;
  updatedAt?: string;
}

export interface CharacterForSale {
  id: string;
  name: string;
  nameReading?: string;
  nameEnglish?: string;
  category?: 'original' | 'vtuber';
  thumbnailUrl?: string;
  bio?: string;
  salePrice: number;
  // XOR-Pix encrypted image fields
  noise1Url?: string;
  noise2Url?: string;
  rid?: string;
  imageWidth?: number;
  imageHeight?: number;
  fallbackNoise1Url?: string;
  fallbackNoise2Url?: string;
  // Card theme
  cardTheme?: 'dark' | 'light';
  // Creator info
  creatorId: string;
  creatorName?: string;
  creatorUsername?: string;
  creatorAvatar?: string;
  // Owner info (seller)
  ownerId: string;
  ownerName?: string;
  ownerUsername?: string;
  ownerAvatar?: string;
  tags: string[];
  createdAt?: string;
}

export interface CharactersForSaleResponse {
  characters: CharacterForSale[];
  hasMore: boolean;
}

export interface AdoptionHistoryItem {
  id: string;
  characterId: string;
  characterName?: string;
  characterThumbnail?: string;
  fromOwnerId: string;
  fromOwnerName?: string;
  toOwnerId: string;
  toOwnerName?: string;
  price: number;
  adoptedAt?: string;
}

// ========== Sale Settings API ==========

/**
 * Update sale settings for a character
 * おむかえ販売設定を更新
 */
export const updateSaleSettings = async (
  characterId: string,
  settings: SaleSettings
): Promise<SaleSettingsResponse> => {
  const token = await getAuthToken();

  const response = await fetch(
    `${config.apiBaseUrl}/api/orifoli/characters/${characterId}/sale`,
    {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update sale settings: ${errorText}`);
  }

  return response.json();
};

// ========== Characters For Sale API ==========

export interface ListCharactersForSaleParams {
  limit?: number;
  before?: string;
  search?: string;
  category?: 'original' | 'vtuber';
}

/**
 * List characters available for adoption
 * おむかえ可能なキャラクター一覧を取得（認証不要）
 */
export const listCharactersForSale = async (
  params: ListCharactersForSaleParams = {}
): Promise<CharactersForSaleResponse> => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.before) queryParams.append('before', params.before);
  if (params.search) queryParams.append('search', params.search);
  if (params.category) queryParams.append('category', params.category);

  const url = `${config.apiBaseUrl}/api/orifoli/public/characters/for-sale${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to list characters for sale: ${errorText}`);
  }

  return response.json();
};

// ========== Adoption History API ==========

/**
 * Get adoption history for a character
 * キャラクターのおむかえ履歴を取得（認証不要）
 */
export const getAdoptionHistory = async (
  characterId: string,
  limit: number = 20
): Promise<{ history: AdoptionHistoryItem[] }> => {
  const response = await fetch(
    `${config.apiBaseUrl}/api/orifoli/characters/${characterId}/adoption-history?limit=${limit}`,
    {
      method: 'GET',
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get adoption history: ${errorText}`);
  }

  return response.json();
};

// ========== Helper Functions ==========

/**
 * Format price for display
 */
export const formatPrice = (price: number): string => {
  return `¥${price.toLocaleString()}`;
};

/**
 * Validate sale price
 */
export const validateSalePrice = (price: number): { valid: boolean; error?: string } => {
  if (price < 1000) {
    return { valid: false, error: '価格は1,000円以上に設定してください' };
  }
  if (price > 1000000) {
    return { valid: false, error: '価格は1,000,000円以下に設定してください' };
  }
  return { valid: true };
};
